import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UserKeys, deleteUserFromGroupsUserPortal } from '../../api/users-api';
import { handleErrors } from '../../utils/errors';
import { ApiException, Group, Page } from '../../../types';
import SharedDialog from '../../shared/shared-dialog';
import { useStore } from '../state/zustand-store';
import { useAnalytics } from '../../shared/analytics/create-events';
import * as analytics from '../../shared/analytics/analytics.json';
import { ModuleList } from '../../shared/analytics/module-list';
import { Dispatch, SetStateAction } from 'react';
import axios from 'axios';
import { useUserSettings } from '../../utils/use-user-settings';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
interface DeleteMyGroupsDialogProps {
  open: boolean;
  handleClose: () => void;
  selectedGroup: Group;
  setSelectedGroup: Dispatch<SetStateAction<Group | null>>;
  paginationModel: {
    cursor: string | null;
    results: Group[];
    nameSpace: string;
    firstPull: boolean;
    searchTime: number;
    pageSize: number;
  };
}

export default function DeleteMyGroupsDialog({
  open,
  handleClose,
  selectedGroup,
  setSelectedGroup,
  paginationModel,
}: DeleteMyGroupsDialogProps) {
  const { loggedInUser } = useUserSettings();
  const queryClient = useQueryClient();
  const { createEvent } = useAnalytics();
  const { setSnackbar } = useStore();

  const { mutate, isPending } = useMutation({
    mutationFn: () => deleteUserFromGroupsUserPortal(selectedGroup.email),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'You have been removed from the group successfully.',
      });
      const userPortalGroupsQueries = queryClient.getQueriesData<Page<Group[]>>(
        {
          queryKey: [UserKeys.UserGroups, paginationModel, loggedInUser],
        }
      );
      if (userPortalGroupsQueries.length > 0) {
        userPortalGroupsQueries.map((userPortalGroupQuery) => {
          const queryKey = userPortalGroupQuery[0];
          const userPortalGroups = userPortalGroupQuery[1];
          if (
            userPortalGroups?.results.some(
              (group) => group.email === selectedGroup.email
            )
          ) {
            queryClient.setQueryData<Page<Group[]>>(queryKey, (oldData) => {
              if (!oldData) return;
              const newData = {
                ...oldData,
                results: oldData.results.filter(
                  (group) => group.email !== selectedGroup.email
                ),
              };
              return newData;
            });
          }
        });
      }
      setSelectedGroup(null);
      handleClose();
    },
    onError: (error) => {
      if (axios.isAxiosError(error)) {
        const errorResponse = error.response?.data as ApiException;
        errorResponse.isShowable = false;
        handleErrors({
          error,
        });
      }
    },
    onSettled: () => {
      createEvent(analytics.userView.my_groups.delete_groups, {
        module: ModuleList.modules.userView.name,
        section: ModuleList.modules.userView.sections.myGroups,
      });
    },
  });

  return (
    <SharedDialog
      open={open}
      handleClose={handleClose}
      title="Remove From Group"
      dialogContent={
        <Box>
          <Typography>
            {`Do you wish to remove yourself from ${selectedGroup.email}?`}
          </Typography>
        </Box>
      }
      primaryButton={{
        title: 'Remove',
        action: mutate,
        loading: isPending,
        disabled: isPending,
        color: 'error',
      }}
    />
  );
}
